/**
 * Pure helpers for the interactive Leadership report (12 Oct): the URL state
 * (range + filters, so a view can be shared), the change arrows, number
 * formats, sorting, CSV, the chart geometry and the report's colours.
 * Import-free, so `tests/leadership-insights.test.mjs` can load it directly.
 */

// ── URL state ───────────────────────────────────────────────────────────────

export const RANGE_PRESETS = ["7d", "30d", "launch", "custom"] as const;
export type RangePreset = (typeof RANGE_PRESETS)[number];
export const DEFAULT_RANGE: RangePreset = "launch";

export const KIND_FILTERS = ["incident", "request"] as const;
export type KindFilter = (typeof KIND_FILTERS)[number];

/** "live" = the interactive page; "printable" = the stored HTML overview. */
export const VIEWS = ["live", "printable"] as const;
export type ReportView = (typeof VIEWS)[number];

export interface InsightsState {
  range: RangePreset;
  /** Custom range only: New York days, YYYY-MM-DD. */
  from: string | null;
  to: string | null;
  teams: string[];
  channels: string[];
  kind: KindFilter | null;
  sites: string[];
  view: ReportView;
}

export const DEFAULT_STATE: InsightsState = {
  range: DEFAULT_RANGE,
  from: null,
  to: null,
  teams: [],
  channels: [],
  kind: null,
  sites: [],
  view: "live",
};

const DAY = /^\d{4}-\d{2}-\d{2}$/;

/** Whether `value` is a real calendar day written YYYY-MM-DD. */
export function isDay(value: string | null | undefined): value is string {
  if (typeof value !== "string" || !DAY.test(value)) {
    return false;
  }
  const at = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(at.getTime()) && at.toISOString().slice(0, 10) === value;
}

function list(params: URLSearchParams, key: string): string[] {
  const seen = new Set<string>();
  for (const raw of params.getAll(key)) {
    const value = raw.trim();
    if (value) {
      seen.add(value);
    }
  }
  return [...seen].sort((a, b) => a.localeCompare(b));
}

/**
 * The page's state from its query string. Anything unknown or malformed falls
 * back to the default (a custom range without both days, from after to, an
 * unknown kind), so a hand-edited link still opens something sensible.
 */
export function parseState(search: string | URLSearchParams): InsightsState {
  const params = typeof search === "string" ? new URLSearchParams(search) : search;
  const rawRange = params.get("range");
  let range: RangePreset = (RANGE_PRESETS as readonly string[]).includes(rawRange ?? "")
    ? (rawRange as RangePreset)
    : DEFAULT_RANGE;
  let from: string | null = null;
  let to: string | null = null;
  if (range === "custom") {
    const f = params.get("from");
    const t = params.get("to");
    if (isDay(f) && isDay(t) && f <= t) {
      from = f;
      to = t;
    } else {
      range = DEFAULT_RANGE;
    }
  }
  const rawKind = params.get("kind");
  const kind = (KIND_FILTERS as readonly string[]).includes(rawKind ?? "")
    ? (rawKind as KindFilter)
    : null;
  const view: ReportView = params.get("view") === "printable" ? "printable" : "live";
  return {
    range,
    from,
    to,
    teams: list(params, "team"),
    channels: list(params, "channel"),
    kind,
    sites: list(params, "site"),
    view,
  };
}

/** The query string of a state; defaults are left out, lists are sorted. */
export function stateToSearch(state: InsightsState): string {
  const params = new URLSearchParams();
  if (state.view !== "live") {
    params.set("view", state.view);
  }
  if (state.range !== DEFAULT_RANGE) {
    params.set("range", state.range);
  }
  if (state.range === "custom" && state.from && state.to) {
    params.set("from", state.from);
    params.set("to", state.to);
  }
  const add = (key: string, values: string[]) => {
    for (const value of [...values].sort((a, b) => a.localeCompare(b))) {
      params.append(key, value);
    }
  };
  add("team", state.teams);
  add("channel", state.channels);
  if (state.kind) {
    params.set("kind", state.kind);
  }
  add("site", state.sites);
  return params.toString();
}

/** Whether any global filter is set (the range is not a filter). */
export function hasFilters(state: InsightsState): boolean {
  return (
    state.teams.length > 0 ||
    state.channels.length > 0 ||
    state.kind !== null ||
    state.sites.length > 0
  );
}

export interface InsightsApiQuery {
  range: RangePreset;
  from?: string;
  to?: string;
  team?: string[];
  channel?: string[];
  kind?: KindFilter;
  site?: string[];
}

/** The server's query for a state (only what is set). */
export function apiQuery(state: InsightsState): InsightsApiQuery {
  const query: InsightsApiQuery = { range: state.range };
  if (state.range === "custom" && state.from && state.to) {
    query.from = state.from;
    query.to = state.to;
  }
  if (state.teams.length) {
    query.team = [...state.teams];
  }
  if (state.channels.length) {
    query.channel = [...state.channels];
  }
  if (state.kind) {
    query.kind = state.kind;
  }
  if (state.sites.length) {
    query.site = [...state.sites];
  }
  return query;
}

// ── Numbers and changes ─────────────────────────────────────────────────────

export type Better = "up" | "down" | "neutral";
export type Tone = "good" | "bad" | "neutral";

export interface DeltaLike {
  delta?: number | null;
  better: string;
  unit: string;
}

export interface DeltaView {
  arrow: "▲" | "▼" | "＝";
  /** "+3", "−2", "+4.5 pts"; the sign is a real minus. */
  text: string;
  tone: Tone;
}

/** Round to at most one decimal, without a trailing ".0". */
export function round1(value: number): string {
  const r = Math.round(value * 10) / 10;
  return Number.isInteger(r) ? String(r) : r.toFixed(1);
}

/**
 * The ▲/▼ of a tile against its comparison, with the colour of what it means:
 * up is good for "up" measures (HelpDesk share, on time), bad for "down" ones
 * (open, past target); "neutral" ones (raised) stay grey. `null` without a
 * comparison. `pts` is the unit word for a percentage tile.
 */
export function deltaView(tile: DeltaLike, pts = "pts"): DeltaView | null {
  const delta = tile.delta;
  if (delta === null || delta === undefined || Number.isNaN(delta)) {
    return null;
  }
  const better = tile.better as Better;
  const tone: Tone =
    delta === 0 || better === "neutral"
      ? "neutral"
      : delta > 0 === (better === "up")
        ? "good"
        : "bad";
  const arrow = delta > 0 ? "▲" : delta < 0 ? "▼" : "＝";
  const sign = delta > 0 ? "+" : delta < 0 ? "−" : "±";
  const magnitude = round1(Math.abs(delta));
  return { arrow, text: `${sign}${magnitude}${tile.unit === "pct" ? ` ${pts}` : ""}`, tone };
}

/** A tile's value: a whole percentage ("62%") or a count; "–" when none. */
export function formatValue(value: number | null | undefined, unit: string): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return "–";
  }
  return unit === "pct" ? `${Math.round(value)}%` : String(Math.round(value));
}

/** Whole-number share of `n` in `d` (0 when `d` is 0), as the report rounds it. */
export function pctOf(n: number, d: number): number {
  return d ? Math.round((100 * n) / d) : 0;
}

/** A ticket number as staff say it: "INC 1234" / "SR 10653". */
export function ticketLabel(kind: string, number: number | null | undefined): string {
  const prefix = kind === "Incident" ? "INC" : "SR";
  return number === null || number === undefined ? prefix : `${prefix} ${number}`;
}

/** The HelpDesk ticket page of a drill-in row, or null without an id. */
export function ticketHref(ticketId: string | null | undefined): string | null {
  return ticketId ? `/tickets/${encodeURIComponent(ticketId)}` : null;
}

// ── Sorting and CSV ─────────────────────────────────────────────────────────

export type SortDirection = "asc" | "desc";

/**
 * Rows sorted by one key, stably. Numbers compare as numbers, ISO dates as
 * text (which orders them), everything else by locale; blanks always last.
 */
export function sortRows<T extends object>(
  rows: readonly T[],
  key: keyof T,
  direction: SortDirection,
): T[] {
  const factor = direction === "asc" ? 1 : -1;
  const blank = (v: unknown) => v === null || v === undefined || v === "";
  return rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => {
      const x = a.row[key] as unknown;
      const y = b.row[key] as unknown;
      if (blank(x) || blank(y)) {
        return blank(x) && blank(y) ? a.index - b.index : blank(x) ? 1 : -1;
      }
      let order: number;
      if (typeof x === "number" && typeof y === "number") {
        order = x - y;
      } else if (typeof x === "boolean" && typeof y === "boolean") {
        order = Number(x) - Number(y);
      } else {
        order = String(x).localeCompare(String(y), undefined, { numeric: true });
      }
      return order === 0 ? a.index - b.index : order * factor;
    })
    .map(({ row }) => row);
}

function csvCell(value: unknown): string {
  if (value === null || value === undefined) {
    return "";
  }
  const text = Array.isArray(value) ? value.join(" -> ") : String(value);
  // Spreadsheet formula injection: a leading = + - @ is read as a formula.
  const safe = /^[=+\-@]/.test(text) ? `'${text}` : text;
  return /[",\n\r;]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

/** CSV with a header row (and a BOM, so Excel reads UTF-8). */
export function toCsv<T extends object>(
  rows: readonly T[],
  columns: readonly { key: keyof T; label: string }[],
): string {
  const lines = [columns.map((c) => csvCell(c.label)).join(",")];
  for (const row of rows) {
    lines.push(columns.map((c) => csvCell(row[c.key])).join(","));
  }
  return `\uFEFF${lines.join("\r\n")}\r\n`;
}

// ── Chart geometry ──────────────────────────────────────────────────────────

export interface Arc {
  index: number;
  /** Dash length and the offset it starts at, along the circle. */
  length: number;
  offset: number;
}

/** The donut's arcs, clockwise from 12 o'clock; empty parts get none. */
export function donutArcs(values: readonly number[], circumference: number): Arc[] {
  const total = values.reduce((sum, v) => sum + Math.max(0, v), 0) || 1;
  const arcs: Arc[] = [];
  let offset = 0;
  values.forEach((raw, index) => {
    const v = Math.max(0, raw);
    if (!v) {
      return;
    }
    const length = (circumference * v) / total;
    arcs.push({ index, length, offset });
    offset += length;
  });
  return arcs;
}

/** The goal tick across a ring at `goal` %: two points (x1, y1, x2, y2). */
export function goalTick(
  goal: number,
  size: number,
  width: number,
): [number, number, number, number] {
  const r = (size - width) / 2;
  const a = ((goal / 100) * 360 - 90) * (Math.PI / 180);
  const c = size / 2;
  const inner = r - width / 2 - 3;
  const outer = r + width / 2 + 3;
  return [
    c + inner * Math.cos(a),
    c + inner * Math.sin(a),
    c + outer * Math.cos(a),
    c + outer * Math.sin(a),
  ];
}

/** Whether a day is a Saturday or Sunday (YYYY-MM-DD). */
export function isWeekend(day: string): boolean {
  const d = new Date(`${day}T12:00:00Z`).getUTCDay();
  return d === 0 || d === 6;
}

// ── The report's colours (the 9 Oct overview) ───────────────────────────────

export const C = {
  incident: "#f76707",
  request: "#1c7ed6",
  done: "#2f9e44",
  open: "#7048e8",
  late: "#e03131",
  migrated: "#adb5bd",
  before: "#ced4da",
  rft: "#0ca678",
  due: "#f59f00",
  track: "#f1f3f5",
} as const;

export const STATUS_COLORS: Record<string, string> = {
  "Open – in progress": C.open,
  "Waiting – customer": "#ae3ec9",
  "Waiting – other": "#d0bfff",
  "Resolved/Fulfilled": "#51cf66",
  Closed: C.done,
  "Cancelled/Withdrawn": C.migrated,
};

export const AGE_COLORS = ["#d0ebff", "#74c0fc", "#1c7ed6", "#1864ab"] as const;

export const CHANNEL_COLORS: Record<string, string> = {
  "IT HelpDesk": "#1c7ed6",
  Email: "#f76707",
  "Phone – AI voice agent": "#ae3ec9",
  "Phone – analyst": "#e599f7",
  "Self Service portal": "#20c997",
};
export const OTHER_COLOR = "#868e96";
export const MAILBOX_COLORS = ["#f76707", "#ffa94d", "#ffd8a8", "#ced4da"] as const;

export function channelColor(group: string): string {
  return CHANNEL_COLORS[group] ?? OTHER_COLOR;
}

// ── Label keys (the server speaks the report's English names) ───────────────

export const STATUS_KEYS = {
  "Open – in progress": "openProgress",
  "Waiting – customer": "waitCustomer",
  "Waiting – other": "waitOther",
  "Resolved/Fulfilled": "resolved",
  Closed: "closed",
  "Cancelled/Withdrawn": "cancelled",
} as const;
export const AGE_KEYS = {
  "< 1 day": "lt1",
  "1–3 days": "d1to3",
  "3–7 days": "d3to7",
  "> 7 days": "gt7",
} as const;
export const CHANNEL_KEYS = {
  "IT HelpDesk": "helpdesk",
  Email: "email",
  "Phone – AI voice agent": "voice",
  "Phone – analyst": "phone",
  "Self Service portal": "portal",
} as const;
export const SLA_STATES = ["met", "missed", "breached", "pending", "none", "n/a"] as const;

export type StatusKey = (typeof STATUS_KEYS)[keyof typeof STATUS_KEYS];
export type AgeKey = (typeof AGE_KEYS)[keyof typeof AGE_KEYS];
export type ChannelKey = (typeof CHANNEL_KEYS)[keyof typeof CHANNEL_KEYS];
export type SlaStateKey = "met" | "missed" | "breached" | "pending" | "none" | "na";

/** A key of `map` for `value`, or undefined (the server's names are open-ended). */
export function keyOf<M extends Record<string, string>>(
  map: M,
  value: string,
): M[keyof M] | undefined {
  return Object.prototype.hasOwnProperty.call(map, value) ? map[value as keyof M] : undefined;
}

/** The label key of an SLA state ("n/a" → "na"); unknown states read as "none". */
export function slaStateKey(state: string): SlaStateKey {
  if (state === "n/a") {
    return "na";
  }
  return (
    (["met", "missed", "breached", "pending", "none"] as const).find((s) => s === state) ?? "none"
  );
}

export const TILE_KEYS = ["raised", "done", "open", "past_target", "rft", "share"] as const;
export type TileKey = (typeof TILE_KEYS)[number];
export const DEFINITION_KEYS = [
  ...TILE_KEYS,
  "teams",
  "status",
  "age",
  "flow",
  "sla",
  "due",
  "channels",
  "mailboxes",
  "daily",
  "sites",
  "people",
  "getting",
  "moves",
] as const;
export type DefinitionKey = (typeof DEFINITION_KEYS)[number];
export const TEAM_COLUMNS = ["open", "waiting", "older", "late", "migrated", "in", "out"] as const;
export type TeamColumn = (typeof TEAM_COLUMNS)[number];

/** The mailbox name of an email channel label ("Email-Global" → "Global"); "" for other email. */
export function mailboxName(channel: string): string {
  if (channel === "Email-other") {
    return "";
  }
  return channel.startsWith("Email-") ? channel.slice("Email-".length) : channel;
}

// ── The 16 Oct views: sites, requesters, what we're getting, moves ─────────

/** The site key of a ticket with no site (the server's `NO_SITE`). */
export const NO_SITE = "(no site)";

/** A count change as the report writes it: "+3", "−2" (real minus), "0". */
export function changeText(delta: number): string {
  if (delta > 0) {
    return `+${delta}`;
  }
  return delta < 0 ? `−${Math.abs(delta)}` : "0";
}

/**
 * A time in hours as a short duration: hours below two days ("5.5 h"), whole
 * and half days above ("3.5 d"); null when unknown.
 */
export function durationParts(
  hours: number | null | undefined,
): { unit: "h" | "d"; value: string } | null {
  if (hours === null || hours === undefined || Number.isNaN(hours)) {
    return null;
  }
  if (hours < 48) {
    return { unit: "h", value: round1(hours) };
  }
  return { unit: "d", value: round1(Math.round((hours / 24) * 2) / 2) };
}

/** A ticket's team moves as one line: "Global IT Helpdesk → Systems Support → Network". */
export function movesLine(moves: readonly { from_team: string; to_team: string }[]): string {
  if (moves.length === 0) {
    return "";
  }
  return [moves[0]!.from_team, ...moves.map((m) => m.to_team)].join(" → ");
}

/** The drill-in value of a "requests by offering" cell: `week|<offering>`. */
export function offeringValue(window: "week" | "last_week" | "launch", offering: string): string {
  return `${window}|${offering}`;
}
